import RangeSlider from '../common/RangeSlider';

/**
 * The mid-session difficulty range (FR-027–FR-030), in whichever of its homes is on screen.
 *
 * There are three, and they are the same control: the table frame's `controls` row on the bands whose
 * height can carry it (`DIFFICULTY_RANGE_INLINE` — see difficultyRangeGates.ts), the masthead's popup
 * on the bands that cannot, and the no-problems-in-range state, which has no frame at all and must
 * carry it regardless — widening the range is the only way out of that state.
 *
 * ONE component, because the pre-023 page had four copies of this markup on four breakpoints and
 * they drifted: different labels ("Difficulty" / "Difficulty Range"), different type scales, and a
 * numeric row present in some and stripped in others. What varies legitimately is the SURFACE it
 * sits on, which is one prop.
 */
export function DifficultyRangeControl({
  minLevel,
  maxLevel,
  disabled = false,
  surface,
  className = '',
  onChange,
}: {
  minLevel: number;
  maxLevel: number;
  /** Serial-search sessions pin the range to the hand they asked for (FR-030). */
  disabled?: boolean;
  /**
   * `felt` for the dark table — inside the frame's controls row, or the felt card of the
   * no-problems state. `light` for the cream popup dialog, where the felt variant's cream-soft
   * labels would be invisible.
   */
  surface: 'felt' | 'light';
  /** The caller's hook — `difficulty-slider-inline` on the two INLINE homes, so an e2e selector
   *  for "the control is on the page" cannot be satisfied by the popup copy. */
  className?: string;
  onChange: (min: number, max: number) => void;
}) {
  const onFelt = surface === 'felt';
  return (
    <div className={className}>
      <div className="flex items-center justify-between">
        <span className={`text-xs font-medium ${onFelt ? 'text-cream' : 'text-ink'}`}>
          Difficulty Range
        </span>
        {/* Brass by surface: `brass-on-felt` is 4.9:1 on the table, `brass-text` 5.2:1 on cream.
            Plain `brass` is decorative at 2.3-3.5:1 and belongs to neither. */}
        <span className={`text-xs font-bold ${onFelt ? 'text-brass-on-felt' : 'text-brass-text'}`}>
          {minLevel} - {maxLevel}
        </span>
      </div>
      <RangeSlider
        min={1}
        max={10}
        minValue={minLevel}
        maxValue={maxLevel}
        disabled={disabled}
        variant={surface}
        onChange={onChange}
      />
    </div>
  );
}
